<script setup lang="ts">
/**
 * ScoreEditToolbar
 *
 * Persistente Aktions-Werkzeugleiste der interaktiven Vorschau (Word/MuseScore-artig): operiert auf
 * der gesamten (Mehrfach-)Auswahl – Transponieren ±Halbton/±Oktave, Diatonisch (Akkordton
 * hinzufügen / Dreiklang reduzieren), Kopieren/Einfügen, Bindebogen, Note teilen, Löschen – plus die Öffner für das kontextuelle Akkord-/Tonart-Panel und „Auswahl
 * aufheben". Ersetzt den früheren `SelectionIndicator`-Chip. State/Aktionen kommen per inject aus
 * dem {@link ScoreEditContext}; Einzelnoten-Aktionen (Bindebogen/Teilen) sind nur bei Auswahl der
 * Größe 1 aktiv. Sichtbar nur bei bestehender Auswahl.
 *
 * Styling bewusst als Template-Klassen (keine `@apply` im scoped-Style): Tailwind v4 generiert
 * Utilities nur aus dem Template-Scan – ein ausschließlich via `@apply` genutztes `h-8` wäre sonst
 * eine „unknown utility" im Build.
 */
import {computed, ref} from 'vue';
import {onKeyStroke} from '@vueuse/core';
import {Icon} from '@iconify/vue';
import {injectScoreEditContext} from './editContext';
import {injectToolbarExtensions} from './toolbarExtensionsContext';
import ToolbarGroup from './ToolbarGroup.vue';
import {DIATONIC_INTERVAL_OPTIONS, TRIAD_REDUCTION_OPTIONS} from '@/components/transposition/diatonicShiftOptions';
import {TriadReductionEnum} from '@/generated';
import TieIcon from './icons/TieIcon.vue';
import SplitNoteIcon from './icons/SplitNoteIcon.vue';
import MergeNotesIcon from './icons/MergeNotesIcon.vue';
import TimeSignatureIcon from './icons/TimeSignatureIcon.vue';
import {EDITABLE_VERSE_NUMBERS, BASS_LYRIC_NUMBER} from '@/composables/musicXmlDom';

// `ts-key-enum` ist ein reines Typ-Paket (ambient const enum ohne Laufzeit-JS) und unter
// `verbatimModuleSyntax` nicht als WERT importierbar – dieselben Namen deshalb lokal.
const Key = {
  ArrowUp: 'ArrowUp', ArrowDown: 'ArrowDown', ArrowLeft: 'ArrowLeft', ArrowRight: 'ArrowRight',
} as const;

const {
  selectedNote, selectedNotes, selectedMeasures, selectedMeasure, copiedSequence,
  transposeSelection, addDiatonicTones, reduceTriads, removeExtremeChordTones, copySelection, pasteSelection, pasteNoteReplace, requestDelete,
  toggleTie, splitNote, mergeNote, mergeTiedNotes, halveDurations, doubleDurations, openChordEditor, openKeySignatureEditor, openTimeSignatureEditor,
  openStructureEditor, openMeasuresEditor,
  openBassStyleEditor, hasBassPreview, clearSelection,
  activeVerse,
  selectPreviousNote, selectNextNote,
  extendSelectionByNote, extendSelectionByMeasure, extendSelectionByPart, hasMultipleParts,
} = injectScoreEditContext();

// Entrümpelung der AUSGABE-Vorschau: dort werden Transponieren/Akkord-Tonart-Takt/Diatonisch
// ausgeblendet, damit der Blick auf dem Wesentlichen bleibt. Bewusst ein PROP und nicht mehr
// `useTabStore().isInputTabOpen`: die Antwort steht für eine gemountete Toolbar ohnehin fest
// (der inaktive Tab wird abgebaut), und als globaler Store-Zugriff hing sie unsichtbar an der
// `v-if`-Wahl in `PreviewTabsCard`. Jetzt stehen alle „was bietet diese Vorschau"-Entscheidungen
// beisammen in `PreviewPane`. **Kein Capability-Gate:** die zugehörigen KURZBEFEHLE (↑/↓,
// Shift+↑/↓, Alt+↑/↓) bleiben auch auf der Ausgabe aktiv – anders als beim Playback, das dort
// echt abgeschaltet ist (`enablePlayback`).
const {showAdvancedTools = true} = defineProps<{ showAdvancedTools?: boolean }>();
// Die drei Zusatz-Werkzeuge aus EINEM Kontext (Griff/Bass nur mit Ausgabe-Provider aktiv,
// Gesangstext auf beiden Vorschauen). Ohne Provider ist alles null → die Buttons entfallen.
const toolbarExtensions = injectToolbarExtensions(null);
const grip = toolbarExtensions?.grip ?? null;
const gripsEnabled = computed(() => grip?.enabled.value ?? false);
const canEditGrip = computed(() => grip?.canEdit.value ?? false);

function openGrip(): void {
  grip?.open();
}

const bass = toolbarExtensions?.bass ?? null;
const bassEnabled = computed(() => bass?.enabled.value ?? false);
const canEditBass = computed(() => bass?.canEdit.value ?? false);

function openBass(): void {
  bass?.open();
}

// Die Strophe kommt aus dem geteilten ScoreEditContext, die Freischaltung aus dem Toolbar-Kontext.
const lyric = toolbarExtensions?.lyric ?? null;
const lyricEnabled = computed(() => lyric?.enabled.value ?? false);
const canEditLyric = computed(() => lyric?.canEdit.value ?? false);
const lyricTitle = computed(() => (canEditLyric.value
    ? 'Lyrics eingeben (Strg+L)'
    : lyric?.blockedReason.value || 'Lyrics eingeben (Strg+L)'));

function openLyric(): void {
  lyric?.open();
}

const count = computed(() => selectedNotes.value.length || (selectedNote.value ? 1 : 0));
const hasSelection = computed(() => count.value > 0 || selectedMeasure.value !== null);
const hasNotes = computed(() => count.value > 0);
const isSingle = computed(() => count.value === 1 && !!selectedNote.value);
const isSinglePitched = computed(() => isSingle.value && !selectedNote.value?.isRest);
const canPaste = computed(() => copiedSequence.value.length > 0);
const canReplace = computed(() => canPaste.value && hasNotes.value);

const selectionLabel = computed(() => {
  const measures = selectedMeasures.value
  const labelMeasures = measures.length ? `Takt ${measures.join(', ')} | ` : ''

  const label = selectedMeasure.value !== null ? `${labelMeasures} Takt (single) ${selectedMeasure.value} | ` : labelMeasures

  if (count.value > 1) return `${label} ${count.value} Noten`;
  if (count.value === 1) return `${label} 1 Note`;
  return label;
});

function splitSelected(): void {
  if (selectedNote.value) splitNote(selectedNote.value);
}

function mergeSelected(): void {
  if (selectedNote.value) mergeNote(selectedNote.value);
}

// Diatonisch (Auswahl-Variante der diatonischen Verschiebung, rein clientseitig): Intervall + Modus
// sind lokaler Toolbar-State (Defaults wie das Ganz-Score-Panel: Terz ↑ / Tief + Hoch).
const diatonicInterval = ref<number>(3);
const triadReduction = ref<TriadReductionEnum>(TriadReductionEnum.LowHigh);

function addDiatonic(): void {
  addDiatonicTones(diatonicInterval.value);
}

function reduceTriadSelection(): void {
  reduceTriads(triadReduction.value);
}

function removeHighestChordTone(): void {
  removeExtremeChordTones('highest');
}

function removeLowestChordTone(): void {
  removeExtremeChordTones('lowest');
}

// ============ Globale Edit-/Selektions-Tastenkürzel ============
// Registriert, solange diese Toolbar gemountet (= sichtbar) ist – Button und Kürzel liegen so an
// einem Ort. Prädikate statt String-Keys, da `e.key` bei gedrückter Umschalttaste die Großschreibung
// liefert ('V' statt 'v'). Guard `isEditingText()` überspringt fokussierte INPUT/TEXTAREA/
// contenteditable/CodeMirror-Felder, damit dort die normale Text-Zwischenablage + Slider intakt
// bleiben. Undo/Redo (Strg+Z/Y) und Leertaste/Play-Pause liegen bewusst weiterhin in
// MusicXmlPreview bzw. den OsmdPlaybackControls, da deren State dort lebt.
// Anzeige der Kürzel für den Benutzer: `keyboardShortcuts.ts` (KeyboardShortcutsDialog). Wird hier
// ein Kürzel geändert/ergänzt, dort nachziehen.
function isEditingText(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  return el.tagName === 'INPUT'
      || el.tagName === 'TEXTAREA'
      || el.tagName === 'SELECT'
      || el.isContentEditable
      || !!el.closest('.cm-editor');
}

// Strg+C: gesamte (Mehrfach-)Auswahl als Sequenz in die Zwischenablage kopieren.
onKeyStroke((e) => (e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'c', (e) => {
  if (isEditingText() || !hasNotes.value) return;
  e.preventDefault();
  copySelection();
});
// Strg+Umschalt+V: kopierte Sequenz nach der primären Auswahl einfügen (taktlängen-neutral).
onKeyStroke((e) => (e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'v', (e) => {
  if (isEditingText() || !selectedNote.value) return;
  e.preventDefault();
  pasteSelection();
});
// Strg+V: Auswahl durch die Kopie ersetzen. Einzelauswahl → ganze Sequenz inkl. Rhythmus
// (Viertel→2 Achtel); Mehrfachauswahl → zyklisch nur Tonhöhe. Beides taktlängen-neutral.
onKeyStroke((e) => (e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'v', (e) => {
  if (isEditingText() || !hasNotes.value) return;
  e.preventDefault();
  pasteNoteReplace();
});
// Strg+Umschalt+S: ausgewählte Note/Pause teilen (halbieren, längenerhaltend).
onKeyStroke((e) => (e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 's', (e) => {
  if (isEditingText() || !selectedNote.value) return;
  e.preventDefault();
  splitNote(selectedNote.value);
});
// Strg+Umschalt+M: ausgewählte Note/Pause mit der nächsten zusammenführen (längenerhaltend).
onKeyStroke((e) => (e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'm', (e) => {
  if (isEditingText() || !selectedNote.value) return;
  e.preventDefault();
  mergeNote(selectedNote.value);
});
// Strg+Umschalt+H: Haltebögen der Auswahl zusammenfassen. Nur auf der Eingabe (wie Q/W);
// preventDefault, weil Firefox die Kombination mit der Chronik belegt.
onKeyStroke((e) => (e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'h', (e) => {
  if (!showAdvancedTools || isEditingText() || !hasNotes.value) return;
  e.preventDefault();
  mergeTiedNotes();
});
// Strg+B: Bindebogen (Tie/Slur) zur nächsten Note toggeln (nur Einzelauswahl sinnvoll).
// Q / W: Länge der Auswahl halbieren / verdoppeln (MuseScore-Kürzel, ohne Modifier). Anders als die
// übrigen Kürzel NUR auf der Eingabe: der taktübergreifende Reflow verschöbe auf der Griffschrift-
// Ausgabe die Melodie gegen Druck-Markierungen (PedalMark je Takt) und Bass-Träger.
onKeyStroke((e) => !e.ctrlKey && !e.metaKey && !e.altKey && (e.key.toLowerCase() === 'q' || e.key.toLowerCase() === 'w'), (e) => {
  if (!showAdvancedTools || isEditingText() || !hasNotes.value) return;
  e.preventDefault();
  if (e.key.toLowerCase() === 'q') halveDurations();
  else doubleDurations();
});

onKeyStroke((e) => (e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'b', (e) => {
  if (isEditingText() || !selectedNote.value || selectedNote.value.isRest) return;
  e.preventDefault();
  toggleTie();
});
// Entf/Rücktaste: gesamte (Mehrfach-)Auswahl löschen (Noten → gleich lange Pausen).
// Strg+Umschalt+B: Bass-Notation der Auswahl bearbeiten (nur Griffschrift-Ausgabe, Popover in
// MusicXmlPreview). Der Bass-Kontext fehlt auf der Eingabe-Vorschau → kein Effekt.
onKeyStroke((e) => (e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'b', (e) => {
  if (isEditingText() || !bassEnabled.value || !canEditBass.value) return;
  e.preventDefault();
  openBass();
});
// Strg+L: Gesangstext-Eingabe an der primären Auswahl starten (beide Vorschauen). Ohne
// Lyric-Kontext (Vorschau ohne `enableLyricEdit`) wirkungslos.
onKeyStroke((e) => (e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'l', (e) => {
  if (isEditingText() || !lyricEnabled.value || !canEditLyric.value) return;
  e.preventDefault();
  openLyric();
});
// Entf/Rücktaste: Löschen (fragt auf der Eingabe ggf. „Pause oder nachrücken?");
// Umschalt+Entf: Folgenoten ohne Frage nachrücken lassen.
onKeyStroke((e) => e.key === 'Delete' || e.key === 'Backspace', (e) => {
  if (isEditingText() || !hasNotes.value) return;
  e.preventDefault();
  requestDelete({shift: e.shiftKey});
});
// Pfeil hoch/runter (ohne Strg/Alt): Auswahl transponieren (mit Umschalt = ganze Oktave).
onKeyStroke((e) => !e.ctrlKey && !e.metaKey && !e.altKey && (e.key === Key.ArrowUp || e.key === Key.ArrowDown), (e) => {
  if (isEditingText() || !hasNotes.value) return;
  e.preventDefault();
  const step = (e.shiftKey ? 12 : 1) * (e.key === Key.ArrowUp ? 1 : -1);
  transposeSelection(step);
});
// Alt+Pfeil hoch/runter: das gewählte diatonische Intervall (Betrag) nach oben/unten hinzufügen.
onKeyStroke((e) => e.altKey && !e.ctrlKey && !e.metaKey && (e.key === Key.ArrowUp || e.key === Key.ArrowDown), (e) => {
  if (isEditingText() || !hasNotes.value) return;
  e.preventDefault();
  addDiatonicTones(Math.abs(diatonicInterval.value) * (e.key === Key.ArrowUp ? 1 : -1));
});
// Strg+Shift+Pfeil hoch/unten: Auswahl um den Part darüber/darunter erweitern (nur mehrteilig).
onKeyStroke((e) => (e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === Key.ArrowUp || e.key === Key.ArrowDown), (e) => {
  if (isEditingText() || !hasNotes.value) return;
  e.preventDefault();
  extendSelectionByPart(e.key === Key.ArrowUp ? -1 : 1); // hoch = Part darüber (kleinerer Staff)
});
// Pfeil links/rechts (ohne Strg): primäre Auswahl zur vorigen/nächsten Note verschieben.
onKeyStroke((e) => !e.ctrlKey && !e.metaKey && (e.key === Key.ArrowLeft || e.key === Key.ArrowRight), (e) => {
  if (isEditingText() || (!selectedNote.value && selectedMeasure.value === null)) return;
  e.preventDefault();
  if (e.key === Key.ArrowRight) selectNextNote();
  else selectPreviousNote();
});
// Strg+Pfeil links/rechts: Auswahl kumulativ um vorige/nächste Note erweitern; mit Umschalt um den Takt.
onKeyStroke((e) => (e.ctrlKey || e.metaKey) && (e.key === Key.ArrowLeft || e.key === Key.ArrowRight), (e) => {
  if (isEditingText() || !hasNotes.value) return;
  e.preventDefault();
  const dir = e.key === Key.ArrowRight ? 1 : -1;
  if (e.shiftKey) extendSelectionByMeasure(dir);
  else extendSelectionByNote(dir);
});
// Escape: Auswahl aufheben.
onKeyStroke('Escape', (e) => {
  if (isEditingText() || !hasSelection.value) return;
  e.preventDefault();
  clearSelection();
});
</script>

<template>
  <div
      class="flex flex-wrap items-center gap-1.5 rounded-lg border border-gray-200 bg-white/90 px-2 py-1 shadow-sm"
      role="toolbar"
      aria-label="Auswahl bearbeiten"
  >

  <!-- Navigation: vorige/nächste Note -->
  <ToolbarGroup label="Navigieren">
    <button type="button" class="tool-btn tool-btn-default max-w-6 max-h-6" title="Vorige Note (Pfeil links)" aria-label="Vorige Note" @click="selectPreviousNote" :disabled="!hasSelection">
      <Icon icon="fa6-solid:angle-left"/>
    </button>
    <button type="button" class="tool-btn tool-btn-default max-w-6 max-h-6" title="Nächste Note (Pfeil rechts)" aria-label="Nächste Note" @click="selectNextNote" :disabled="!hasSelection">
      <Icon icon="fa6-solid:angle-right"/>
    </button>
  </ToolbarGroup>

  <!-- Auswahl kumulativ erweitern -->
  <ToolbarGroup label="Erweitern" class="">
    <button type="button" class=" tool-btn tool-btn-default max-w-6 max-h-6" :disabled="!hasNotes" title="Note davor zur Auswahl (Strg+Pfeil links)" aria-label="Note davor zur Auswahl" @click="extendSelectionByNote(-1)">
      <Icon icon="fa6-solid:angles-left"/>
    </button>
    <button type="button" class="tool-btn tool-btn-default max-w-6 max-h-6" :disabled="!hasNotes" title="Note danach zur Auswahl (Strg+Pfeil rechts)" aria-label="Note danach zur Auswahl" @click="extendSelectionByNote(1)">
      <Icon icon="fa6-solid:angles-right"/>
    </button>
    <button type="button" class="tool-btn tool-btn-default max-w-6 max-h-6" :disabled="!hasNotes" title="Takt davor zur Auswahl (Strg+Umschalt+Pfeil links)" aria-label="Takt davor zur Auswahl" @click="extendSelectionByMeasure(-1)">
      <Icon icon="fa6-solid:backward-step"/>
    </button>
    <button type="button" class="tool-btn tool-btn-default max-w-6 max-h-6" :disabled="!hasNotes" title="Takt danach zur Auswahl (Strg+Umschalt+Pfeil rechts)" aria-label="Takt danach zur Auswahl" @click="extendSelectionByMeasure(1)">
      <Icon icon="fa6-solid:forward-step"/>
    </button>
    <!-- Part darüber/darunter – nur bei mehrteiligen Stücken -->
    <template v-if="hasMultipleParts">
      <button type="button" class="tool-btn tool-btn-default" :disabled="!hasNotes" title="Part darüber zur Auswahl (Strg+Umschalt+Pfeil hoch)" aria-label="Part darüber zur Auswahl" @click="extendSelectionByPart(-1)">
        <Icon icon="fa6-solid:up-long"/>
      </button>
      <button type="button" class="tool-btn tool-btn-default" :disabled="!hasNotes" title="Part darunter zur Auswahl (Strg+Umschalt+Pfeil runter)" aria-label="Part darunter zur Auswahl" @click="extendSelectionByPart(1)">
        <Icon icon="fa6-solid:down-long"/>
      </button>
    </template>
  </ToolbarGroup>

  <!-- Transponieren -->
  <ToolbarGroup label="Transponieren" v-if="showAdvancedTools">
    <button type="button" class="tool-btn tool-btn-default max-w-6 max-h-6" :disabled="!hasNotes" title="Oktave tiefer" aria-label="Oktave tiefer" @click="transposeSelection(-12)">
      <Icon icon="fa6-solid:angles-down"/>
    </button>
    <button type="button" class="tool-btn tool-btn-default max-w-6 max-h-6" :disabled="!hasNotes" title="Halbton tiefer (Pfeil runter)" aria-label="Halbton tiefer" @click="transposeSelection(-1)">
      <Icon icon="fa6-solid:angle-down"/>
    </button>
    <button type="button" class="tool-btn tool-btn-default max-w-6 max-h-6" :disabled="!hasNotes" title="Halbton höher (Pfeil hoch)" aria-label="Halbton höher" @click="transposeSelection(1)">
      <Icon icon="fa6-solid:angle-up"/>
    </button>
    <button type="button" class="tool-btn tool-btn-default max-w-6 max-h-6" :disabled="!hasNotes" title="Oktave höher" aria-label="Oktave höher" @click="transposeSelection(12)">
      <Icon icon="fa6-solid:angles-up"/>
    </button>
  </ToolbarGroup>

  <!-- Diatonisch: Akkordton hinzufügen (Terz/Sexte/…) bzw. Dreiklang reduzieren – nur auf der Auswahl -->

  <!-- Zwischenablage -->
  <ToolbarGroup label="Zwischenablage">
    <button type="button" class="tool-btn tool-btn-default" :disabled="!hasNotes" title="Kopieren (Strg+C)" aria-label="Kopieren" @click="copySelection">
      <Icon icon="fa6-solid:copy"/>
    </button>
    <button type="button" class="tool-btn tool-btn-default" :disabled="!canPaste" title="Einfügen (Strg+Umschalt+V)" aria-label="Einfügen" @click="pasteSelection">
      <Icon icon="fa6-solid:paste"/>
    </button>
    <button type="button" class="tool-btn tool-btn-default" :disabled="!canReplace" title="Ersetzen (Strg+V)" aria-label="Ersetzen" @click="pasteNoteReplace">
      <Icon icon="fa6-solid:right-left"/>
    </button>
  </ToolbarGroup>

  <!-- Noten-Aktionen: Bindebogen + Note teilen + Noten zusammenführen (eigene SVG-Icons) + Länge halbieren/verdoppeln -->
  <ToolbarGroup label="Note">
    <button type="button" class="tool-btn tool-btn-default" :disabled="!isSinglePitched" title="Bindebogen zur nächsten Note (Strg+B)" aria-label="Bindebogen" @click="toggleTie">
      <TieIcon/>
    </button>
    <button type="button" class="tool-btn tool-btn-default" :disabled="!isSingle" title="Note/Pause teilen (Strg+Umschalt+S)" aria-label="Note/Pause teilen" @click="splitSelected">
      <SplitNoteIcon/>
    </button>
    <button type="button" class="tool-btn tool-btn-default" :disabled="!isSingle" title="Note/Pause mit der nächsten zusammenführen (Strg+Umschalt+M)" aria-label="Note/Pause mit der nächsten zusammenführen" @click="mergeSelected">
      <MergeNotesIcon/>
    </button>
    <button v-if="showAdvancedTools" type="button" class="tool-btn tool-btn-default" :disabled="!hasNotes" title="Haltebögen der Auswahl zusammenfassen (Strg+Umschalt+H)" aria-label="Haltebögen zusammenfassen" @click="mergeTiedNotes">
      <Icon icon="lucide:link-2"/>
    </button>
    <button v-if="showAdvancedTools" type="button" class="tool-btn tool-btn-default" :disabled="!hasNotes" title="Länge halbieren (Q)" aria-label="Länge halbieren" @click="halveDurations">
      <Icon icon="lucide:fold-horizontal"/>
    </button>
    <button v-if="showAdvancedTools" type="button" class="tool-btn tool-btn-default" :disabled="!hasNotes" title="Länge verdoppeln (W)" aria-label="Länge verdoppeln" @click="doubleDurations">
      <Icon icon="lucide:unfold-horizontal"/>
    </button>
  </ToolbarGroup>

  <!-- Löschen -->
  <ToolbarGroup label="Löschen">
    <button type="button" :class="`text-red-500  tool-btn`" :disabled="!hasNotes" :title="showAdvancedTools ? 'Löschen (Entf; Umschalt+Entf = Folgenoten nachrücken)' : 'Löschen (Entf)'" aria-label="Löschen" @click="requestDelete()">
      <Icon icon="fa6-solid:trash"/>
    </button>
  </ToolbarGroup>

  <!-- Akkord/Tonart/Taktart-Panel öffnen -->
  <ToolbarGroup label="Akkord/Tonart/Takt" v-if="showAdvancedTools">
    <button type="button" :class="`text-purple-600 tool-btn`" title="Akkord bearbeiten" aria-label="Akkord bearbeiten" @click="openChordEditor" :disabled="selectedMeasure == null">
      <Icon class="scale-120" icon="game-icons:guitar-head"/>
    </button>
    <button type="button" :class="`text-amber-600 tool-btn`" title="Tonart bearbeiten" aria-label="Tonart bearbeiten" @click="openKeySignatureEditor" :disabled="selectedMeasure == null">
      <Icon icon="fa6-solid:key"/>
    </button>
    <button type="button" :class="`text-sky-600 tool-btn`" title="Taktart bearbeiten" aria-label="Taktart bearbeiten" @click="openTimeSignatureEditor" :disabled="selectedMeasure == null">
      <TimeSignatureIcon/>
    </button>
    <button type="button" :class="`text-teal-600 tool-btn`" title="Wiederholungen & Taktstriche bearbeiten" aria-label="Wiederholungen & Taktstriche bearbeiten" @click="openStructureEditor" :disabled="selectedMeasure == null">
      <Icon icon="fa6-solid:repeat"/>
    </button>
    <button type="button" :class="`text-rose-600 tool-btn`" title="Takt einfügen/löschen" aria-label="Takt einfügen/löschen" @click="openMeasuresEditor" :disabled="selectedMeasure == null">
      <Icon icon="fa6-solid:table-cells-large"/>
    </button>
    <button v-if="hasBassPreview" type="button" :class="`text-violet-600 tool-btn`" title="Bass-Spielweise dieses Takts" aria-label="Bass-Spielweise dieses Takts" @click="openBassStyleEditor" :disabled="selectedMeasure == null">
      <Icon icon="game-icons:f-clef"/>
    </button>
  </ToolbarGroup>

  <ToolbarGroup label="Diatonisch" v-if="showAdvancedTools">
    <select
        v-model.number="diatonicInterval"
        class="tool-select"
        title="Diatonisches Intervall des hinzuzufügenden Tons"
        aria-label="Diatonisches Intervall"
        :disabled="!hasNotes"
    >
      <option v-for="opt in DIATONIC_INTERVAL_OPTIONS" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
    </select>
    <button type="button" class="tool-btn text-violet-700" :disabled="!hasNotes" title="Ton diatonisch hinzufügen (Alt+Pfeil hoch/runter)" aria-label="Ton diatonisch hinzufügen" @click="addDiatonic">
      <Icon icon="fa6-solid:layer-group"/>
    </button>
    <select
        v-model="triadReduction"
        class="tool-select"
        title="Reduktionsmodus: welche zwei Töne des Dreiklangs bleiben"
        aria-label="Reduktionsmodus"
        :disabled="!hasNotes"
    >
      <option v-for="opt in TRIAD_REDUCTION_OPTIONS" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
    </select>
    <button type="button" class="tool-btn text-violet-700" :disabled="!hasNotes" title="Dreiklang auf zwei Töne reduzieren" aria-label="Dreiklang reduzieren" @click="reduceTriadSelection">
      <Icon icon="fa6-solid:compress"/>
    </button>
    <button type="button" class="tool-btn text-violet-700" :disabled="!hasNotes" title="Höchsten Ton aus dem Akkord entfernen (jeder Akkord ab zwei Tönen)" aria-label="Höchste Note entfernen" @click="removeHighestChordTone">
      <Icon icon="fa6-solid:angles-up"/>
    </button>
    <button type="button" class="tool-btn text-violet-700" :disabled="!hasNotes" title="Tiefsten Ton aus dem Akkord entfernen (jeder Akkord ab zwei Tönen)" aria-label="Niedrigste Note entfernen" @click="removeLowestChordTone">
      <Icon icon="fa6-solid:angles-down"/>
    </button>
  </ToolbarGroup>

  <!-- Griff der Ausgabe-Note ändern (nur auf der Griffschrift-Ausgabe: enableGrips-Provider) -->
  <ToolbarGroup v-if="gripsEnabled" label="Griff" class="">
    <button type="button" :class="`text-emerald-600 tool-btn`" :disabled="!canEditGrip" title="Griff der ausgewählten Note ändern" aria-label="Griff ändern" @click="openGrip">
      <Icon icon="fa6-solid:hand"/>
    </button>
  </ToolbarGroup>

  <!-- Bass-Notation (nur Ausgabe-Vorschau, via BassToolbarContext) -->
  <ToolbarGroup v-if="bassEnabled" label="Bass">
    <button type="button" :class="`text-indigo-600 tool-btn`" :disabled="!canEditBass" title="Bass-Notation der Auswahl bearbeiten (Strg+Umschalt+B)" aria-label="Bass-Notation bearbeiten" @click="openBass">
      <Icon icon="game-icons:f-clef"/>
    </button>
  </ToolbarGroup>

  <!-- Gesangstext (beide Vorschauen, via LyricToolbarContext) -->
  <ToolbarGroup v-if="lyricEnabled" label="Lyrics">
    <!--    <select v-model="activeVerse" class="tool-select" title="Strophe" aria-label="Strophe">-->
    <!--      <option :value="verse" v-for="verse in EDITABLE_VERSE_NUMBERS">Strophe {{ Number.parseInt(verse)! - Number.parseInt(BASS_LYRIC_NUMBER)! }}</option>-->
    <!--    </select>-->
    <button type="button" class="text-rose-600 tool-btn" :disabled="!canEditLyric" :title="lyricTitle" aria-label="Lyrics eingeben" @click="openLyric">
      <Icon icon="ic:baseline-lyrics"/>
    </button>
  </ToolbarGroup>

  <!-- Auswahl aufheben -->
  <ToolbarGroup label="Auswahl">
    <button type="button" class="tool-btn tool-btn-default" title="Auswahl aufheben (Esc)" aria-label="Auswahl aufheben" @click="clearSelection" :disabled="!hasSelection">
      <Icon icon="fa6-solid:xmark"/>
    </button>
  </ToolbarGroup>

  <span class="mr-1 inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700" v-if="selectionLabel.length">
      <Icon icon="fa6-solid:object-group" class="text-[0.7rem]"/>
      {{ selectionLabel }}
    </span>
  </div>
</template>

<style scoped>
@import 'tailwindcss';

.tool-btn {
  @apply flex h-8 w-8 items-center justify-center rounded-md text-sm transition-all shadow-md
  border-1
  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current
  disabled:cursor-not-allowed disabled:opacity-50 disabled:border-gray-300/10 disabled:shadow-sm disabled:grayscale-50
  disabled:hover:grayscale-100
  border-current/40 hover:border-current/60 bg-current/3
  hover:scale-101 hover:bg-current/10 hover:contrast-150 hover:saturate-150 disabled:text-gray-600 disabled:hover:bg-transparent;

}

.tool-btn-default {
  @apply text-blue-800
}

.tool-select {
  @apply h-5 max-w-min rounded-md border border-violet-300/60 bg-white px-1 text-xs text-violet-800 shadow-sm flex shrink
  focus-visible:outline-none  focus-visible:ring-2 focus-visible:ring-violet-400
  disabled:cursor-not-allowed disabled:opacity-50;
}
</style>